import { AccountData } from 'src/stores/useDriftAccountsStore';
import useAccountData from 'src/hooks/useAccountData';
import { useUpdateCurrentSubaccount } from 'src/hooks/driftActions/user/useUpdateCurrentSubaccount';
import { cj } from 'src/utils/classNames';
import { BigNum, QUOTE_PRECISION_EXP, ZERO } from '@drift-labs/sdk';
import HealthDisplay from 'src/utils/HealthDisplay';
import { AccountListItem } from './AccountListItem';
import { abbreviateAddress } from '@drift/common';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import { useMemo } from 'react';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';
import { usePrivyEmbeddedWalletWithFallback } from 'src/hooks/wallets/privy/mappings/usePrivyEmbeddedWalletWithFallback';
import { useMarginInfoByUserKey } from 'src/hooks/query/accountTradingData/useMarginInfo';

export const SubAccountItem = ({ account }: { account: AccountData }) => {
	const health = account.client?.getHealth() ?? 0;
	const userKey = useAccountData()?.userKey;
	const isSelected = account.userKey === userKey;
	const { updateCurrentSubaccount } = useUpdateCurrentSubaccount();
	const isMobile = useIsMobileScreenSize();
	const embeddedWallet = usePrivyEmbeddedWalletWithFallback();
	const isDevMode = useDevSwitchIsOn();
	const { data: marginInfo } = useMarginInfoByUserKey(account.userKey);

	const handleCloseDrawer = useDrawersStore((s) => s.handleCloseDrawer);

	const handleClick = () => {
		if (account.authority && account.userId !== undefined) {
			updateCurrentSubaccount(account.userId, account.authority);
			if (isMobile) {
				handleCloseDrawer('accounts');
			}
		}
	};

	const isDelegatedToEmbeddedWallet = useMemo(
		() => !!embeddedWallet && account.delegate.equals(embeddedWallet),
		[embeddedWallet, account.delegate]
	);

	const value = BigNum.from(
		marginInfo?.netUsdValue ?? ZERO,
		QUOTE_PRECISION_EXP
	);
	const absValue = Math.abs(value.toNum());
	const displayValue =
		absValue >= 10000
			? value.toMillified()
			: value.toNotional().replace('$', '');

	const nameArea = (
		<div className="flex max-w-[232px] items-center gap-x-xs overflow-hidden">
			<div
				className={cj(
					'size-[6px] flex-shrink-0 rounded-full',
					isSelected ? 'bg-purple-link' : 'bg-transparent'
				)}
			/>
			<div className="flex flex-col gap-y-2xs overflow-hidden">
				<span className="truncate">{account.name}</span>
				{account.isDelegatedTo && (
					<span className="truncate typo-t4 text-text-tertiary">
						{abbreviateAddress(account.authority)}
					</span>
				)}
			</div>
		</div>
	);

	const infoArea = (
		<div className="flex flex-col items-end gap-y-xs text-right typo-t4">
			${displayValue}
			<div className="flex items-center gap-x-xs">
				{isDevMode && isDelegatedToEmbeddedWallet && (
					<span className="typo-b5 text-purple-link">Privy-delegated</span>
				)}
				<HealthDisplay health={health} className="min-w-0" />
			</div>
		</div>
	);

	return (
		<AccountListItem
			nameArea={nameArea}
			infoArea={infoArea}
			onClick={handleClick}
			isSelected={isSelected}
		/>
	);
};
